import type { FC } from 'react';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Box,
  IconButton,
  Typography,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip
} from '@material-ui/core';
import type { SxProps } from '@material-ui/system';
import { useDispatch, useSelector } from 'react-redux';
import { usePopover } from '../hooks/use-popover';
import { CommandMenu as MenuIcon } from '../icons/command-menu';
import { useAuth } from '../hooks/use-auth';
import { useSubscriptionStatus } from '../hooks/use-subscription-status';
import { RootState } from '../store';
import { openBugReportDialog, closeBugReportDialog } from '../slices/bugReportSlice';
import { BugReportDialog } from './quick-actions/bug-report-dialog';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faUser, faListUl, faArrowRightFromBracket, faLifeRing, faCircleExclamation, faBuilding, faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface Organization {
  id: string;
  name: string;
}

interface QuickMenuPopoverProps {
  currentOrganization: Organization;
  darkMode: boolean;
  onLanguageChange: (newLanguage: 'en' | 'fr') => void;
  onOrganizationChange: (organizationId: string) => void;
  onSwitchDirection: () => void;
  onSwitchTheme: () => void;
  organizations: Organization[];
  rtlDirection: boolean;
  sx?: SxProps;
}

interface MenuItem {
  id: string;
  title: string;
  description?: string;
  icon: any;
  path?: string;
  action?: () => void;
  color?: string;
  divider?: boolean;
}

export const QuickMenuPopover: FC<QuickMenuPopoverProps> = (props) => {
  const {
    currentOrganization,
    darkMode,
    onLanguageChange,
    onOrganizationChange,
    onSwitchDirection,
    onSwitchTheme,
    organizations,
    rtlDirection,
    ...other
  } = props;

  const [anchorRef, open, handleOpen, handleClose] = usePopover();
  const navigate = useNavigate();
  const { logout } = useAuth() as any;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPremium } = useSubscriptionStatus();
  const { bugReportDialogOpen } = useSelector((state: RootState) => state.bugReport);

  const handleLogout = async () => {
    try {
      handleClose();
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Something went wrong');
    }
  };

  const handleBugReport = () => {
    dispatch(openBugReportDialog({ element: {}, isEditabled: true, isDuplicated: false }));
    handleClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleClose();
  };

  const menuItems: MenuItem[] = [
    {
      id: 'account',
      title: t('account'),
      icon: () => <FontAwesomeIcon icon={faUser as IconProp} size="xs" />,
      path: '/account',
      color: '#6b7280'
    },
    {
      id: 'organization',
      title: t('organization'),
      icon: () => <FontAwesomeIcon icon={faBuilding as IconProp} size="xs" />,
      path: '/company',
      color: '#6b7280'
    },
    {
      id: 'subscription',
      title: t('subscription'),
      description: hasPremium ? t('premium_plan') : t('free_plan'),
      icon: () => <FontAwesomeIcon icon={faStar as IconProp} size="xs" />,
      path: '/company',
      color: '#f59e0b'
    },
    {
      id: 'projects',
      title: t('projects'),
      icon: () => <FontAwesomeIcon icon={faListUl as IconProp} size="xs" />,
      path: '/projects',
      color: '#10b981'
    },
    {
      id: 'logout',
      title: t('logout'),
      icon: () => <FontAwesomeIcon icon={faArrowRightFromBracket as IconProp} size="xs" />,
      action: handleLogout,
      color: '#dc2626'
    },
    {
      id: 'bugreport',
      title: t('report_a_bug'),
      icon: () => <FontAwesomeIcon icon={faCircleExclamation as IconProp} size="xs" />,
      action: handleBugReport,
      color: '#f59e0b'
    },
    {
      id: 'support',
      title: 'Support',
      description: 'admin@ipoptools.com',
      icon: () => <FontAwesomeIcon icon={faLifeRing as IconProp} size="xs" />,
      color: '#06b6d4'
    }
  ];

  const handleItemClick = (item: MenuItem) => {
    if (item.action) {
      item.action();
    } else if (item.path) {
      handleNavigation(item.path);
    }
  };

  return (
    <>
      <Tooltip title="Account Menu" placement="bottom">
        <IconButton
          onClick={handleOpen}
          ref={anchorRef}
          sx={{
            backgroundColor: 'background.default',
            color: 'primary.main',
            boxShadow: 3,
            border: '1px solid',
            borderColor: 'divider',
            width: 40,
            height: 40,
            '&:hover': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: 6,
              transform: 'scale(1.05)',
              borderColor: 'primary.main'
            },
            transition: 'all 0.2s ease-in-out',
            ...other.sx
          }}
        >
          <MenuIcon />
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom'
        }}
        transformOrigin={{
          horizontal: 'center',
          vertical: 'top'
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 480,
            mt: 1,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <Box sx={{ p: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Account Menu
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Manage your account and settings
          </Typography>
        </Box>
        <Divider />
        <List sx={{ py: 1 }}>
          {menuItems.map((item) => {
            if (item.divider) {
              return <Divider key={item.id} sx={{ my: 1 }} />;
            }

            const Icon = item.icon;

            return (
              <ListItemButton
                key={item.id}
                onClick={() => handleItemClick(item)}
                sx={{
                  py: 1.5,
                  px: 3,
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    transform: 'translateX(4px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <ListItemIcon>
                  {typeof Icon === 'function' ? (
                    <Box sx={{ color: item.color || 'text.primary', fontSize: 20 }}>
                      <Icon />
                    </Box>
                  ) : (
                    <Icon
                      sx={{
                        color: item.color || 'text.primary',
                        fontSize: 20
                      }}
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      {item.title}
                    </Typography>
                  }
                  secondary={
                    item.description && (
                      <Typography variant="caption" color="textSecondary">
                        {item.description}
                      </Typography>
                    )
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </Popover>
      <BugReportDialog
        open={bugReportDialogOpen}
        onClose={() => dispatch(closeBugReportDialog())}
      />
    </>
  );
};